/*
  Behaviour for the embedded contact forms. Shared by every provider — the only
  thing that differs is how the form is submitted, which lives in TRANSPORTS.

  A form opts in with markup, not with code:

    <div data-contact-root="carpe-contact-form-bs">        the namespace for generated classes
      <div data-contact-form-view>                         everything hidden once it succeeds
        <form data-contact-form data-contact-transport="basin" novalidate>
      <div data-contact-success tabindex="-1" hidden>
      <div data-contact-error tabindex="-1" hidden>

  Adding a plain field: write the markup with `data-error-required` (and
  `data-error-type` for a format message). Nothing in here changes.

  Adding a conditional field: wrap it in `...__conditional` with
  `data-conditional="my-field"` and add a CONDITIONS row. Rows are applied in
  order against freshly read values, so a field that depends on another field
  goes after it.

  Adding a character counter: `<p data-contact-counter="field_name">`. The limit
  comes from that field's own `maxlength`.

  Adding a provider: add a TRANSPORTS entry and point `data-contact-transport` at it.
*/
(() => {
  /* `control` is a field name; `when` reads the form's current values */
  const CONDITIONS = [
    {
      field: 'order-email',
      control: 'order_email',
      required: true,
      when: (values) => values.order_email_matches === 'no',
    },
    {
      field: 'subscription-details',
      control: 'subscription_details',
      required: true,
      when: (values) => values.reason === 'subscription',
    },
    {
      field: 'swap-details',
      control: 'swap_details',
      when: (values) => values.subscription_details === 'swap_product',
    },
  ];

  const FALLBACK_COPY = {
    required: 'This field is required',
    type: 'Check the format of this entry',
  };

  const TRANSPORTS = {
    /* Basin's own script owns the request; we only react to its lifecycle events */
    basin(context) {
      const { form } = context;

      document.addEventListener('basinjsFormSubmitted', (event) => {
        if (event.detail.form !== form) return;

        context.setBusy(true);
        context.hideError();
      });

      document.addEventListener('basinjsFormSuccess', (event) => {
        if (event.detail.form !== form) return;

        context.setBusy(false);
        context.showSuccess();
      });

      document.addEventListener('basinjsFormError', (event) => {
        if (event.detail.form !== form) return;

        context.setBusy(false);
        context.showError(event.detail.error);
      });
    },

    /* Formcarry has no script of its own, so post the FormData ourselves */
    formcarry(context) {
      const { form } = context;

      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        context.setBusy(true);
        context.hideError();

        try {
          const response = await fetch(form.action, {
            method: form.method,
            body: new FormData(form),
            headers: { Accept: 'application/json' },
          });

          if (!response.ok) {
            throw new Error(`Formcarry returned ${response.status}`);
          }

          context.setBusy(false);
          context.showSuccess();
        } catch (error) {
          context.setBusy(false);
          context.showError(error);
        }
      });
    },
  };

  function setVisible(element, visible) {
    if (!element) return;

    element.hidden = !visible;
    /* providers that render their own success state set inline display, so clear it too */
    element.style.display = visible ? '' : 'none';
  }

  function init(root) {
    const form = root.querySelector('[data-contact-form]');

    if (!form || root.dataset.initialized === 'true') return;

    const transport = TRANSPORTS[form.dataset.contactTransport];

    if (!transport) {
      console.error('Contact form: unknown transport', form.dataset.contactTransport);

      return;
    }

    root.dataset.initialized = 'true';

    const namespace = root.dataset.contactRoot;
    const errorClass = `${namespace}__field-error`;

    const submitButton = root.querySelector('[data-contact-submit]');
    const submitText = root.querySelector('[data-contact-submit-text]');
    const submitLabel = submitText?.textContent ?? '';
    const formView = root.querySelector('[data-contact-form-view]');
    const successView = root.querySelector('[data-contact-success]');
    const errorView = root.querySelector('[data-contact-error]');
    const counters = [...root.querySelectorAll('[data-contact-counter]')];

    /* resolved once — the set of names never changes, only which are enabled */
    const conditionals = new Map(
      CONDITIONS.map((condition) => [condition, root.querySelector(`[data-conditional="${condition.field}"]`)])
    );
    const fieldNames = [
      ...new Set(
        [...form.elements].filter((el) => el.name && el.type !== 'submit' && el.type !== 'button').map((el) => el.name)
      ),
    ];
    const errorElements = new Map();

    /* a radio group comes back as a RadioNodeList; index 0 carries `required` for the group */
    function controlsOf(name) {
      const entry = form.elements[name];

      if (!entry) return [];

      return entry instanceof RadioNodeList ? [...entry] : [entry];
    }

    function controlOf(name) {
      return controlsOf(name)[0];
    }

    /* RadioNodeList.value is the checked value, so both kinds read the same way */
    function currentValues() {
      return fieldNames.reduce((values, name) => {
        values[name] = form.elements[name].value;

        return values;
      }, {});
    }

    /* ---- inline validation ---- */

    /* the message sits under the control, or under the whole set of choices */
    function anchorOf(control) {
      return control.type === 'radio' ? control.closest('[data-contact-choices]') : control;
    }

    /* error copy hangs off the input, or off the fieldset for a radio group */
    function copyOf(control) {
      const source = control.type === 'radio' ? control.closest('fieldset') : control;

      if (control.validity.typeMismatch) {
        return source.dataset.errorType || FALLBACK_COPY.type;
      }

      return source.dataset.errorRequired || FALLBACK_COPY.required;
    }

    function errorElement(control) {
      const cached = errorElements.get(control.name);

      if (cached) return cached;

      const element = document.createElement('p');

      element.className = errorClass;
      element.id = `${form.id || namespace}-error-${control.name}`;
      element.hidden = true;
      anchorOf(control).insertAdjacentElement('afterend', element);
      errorElements.set(control.name, element);

      return element;
    }

    /* every radio in a group points at the same message, so the group announces it either way */
    function describe(control, errorId) {
      controlsOf(control.name).forEach((field) => {
        if (!('baseDescribedby' in field.dataset)) {
          field.dataset.baseDescribedby = field.getAttribute('aria-describedby') ?? '';
        }

        const ids = [field.dataset.baseDescribedby, errorId].filter(Boolean).join(' ');

        if (ids) {
          field.setAttribute('aria-describedby', ids);
        } else {
          field.removeAttribute('aria-describedby');
        }

        if (errorId) {
          field.setAttribute('aria-invalid', 'true');
        } else {
          field.removeAttribute('aria-invalid');
        }
      });
    }

    function setError(control) {
      const element = errorElement(control);

      element.textContent = copyOf(control);
      element.hidden = false;

      anchorOf(control).toggleAttribute('data-invalid', control.type === 'radio');
      describe(control, element.id);
    }

    function clearError(control) {
      if (!control) return;

      const element = errorElements.get(control.name);

      if (element) {
        element.hidden = true;
      }

      anchorOf(control).removeAttribute('data-invalid');
      describe(control, null);
    }

    /* returns the first invalid control, or null when the form is good to send */
    function validate() {
      let firstInvalid = null;

      fieldNames.forEach((name) => {
        const control = controlOf(name);

        if (!control.willValidate || control.validity.valid) {
          clearError(control);

          return;
        }

        setError(control);
        firstInvalid = firstInvalid || control;
      });

      return firstInvalid;
    }

    function revalidate(control) {
      const primary = controlOf(control.name);

      if (!primary?.hasAttribute('aria-invalid')) return;

      if (primary.validity.valid) {
        clearError(primary);
      } else {
        setError(primary);
      }
    }

    /* ---- conditional fields ---- */

    function applyCondition(condition, show) {
      const container = conditionals.get(condition);
      const controls = controlsOf(condition.control);

      if (!container || !controls.length) return;

      container.toggleAttribute('data-open', show);

      controls.forEach((control, index) => {
        control.disabled = !show;
        control.required = show && Boolean(condition.required) && index === 0;

        if (show) return;

        if (control.type === 'radio') {
          control.checked = false;
        } else {
          control.value = '';
        }
      });

      if (!show) {
        clearError(controls[0]);
      }
    }

    function syncConditionals() {
      /* values are re-read per row so a row can react to one a row above just cleared */
      CONDITIONS.forEach((condition) => applyCondition(condition, condition.when(currentValues())));
    }

    /* ---- counters ---- */

    function syncCounters() {
      counters.forEach((counter) => {
        const control = controlOf(counter.dataset.contactCounter);

        if (!control) return;

        const limit = control.maxLength;

        if (limit < 0) return;

        counter.textContent = `${control.value.length}/${limit}`;
        counter.toggleAttribute('data-at-limit', control.value.length >= limit);
      });
    }

    /* ---- shared state the transports drive ---- */

    /* aria-disabled rather than disabled, so focus is not thrown to the top mid-submit */
    function setBusy(isBusy) {
      submitButton.setAttribute('aria-disabled', String(isBusy));
      form.setAttribute('aria-busy', String(isBusy));

      if (submitText) {
        submitText.textContent = isBusy ? submitText.dataset.busyLabel || 'Sending...' : submitLabel;
      }
    }

    function showSuccess() {
      form.reset();
      fieldNames.forEach((name) => clearError(controlOf(name)));
      syncConditionals();
      syncCounters();

      formView.hidden = true;
      setVisible(successView, true);
      successView.focus();
    }

    function showError(error) {
      setVisible(errorView, true);
      errorView.focus();

      console.error('Contact form submission failed:', error);
    }

    function hideError() {
      setVisible(errorView, false);
    }

    form.addEventListener('input', (event) => {
      revalidate(event.target);
      syncCounters();
    });

    form.addEventListener('change', (event) => {
      revalidate(event.target);
      syncConditionals();
    });

    /* capture phase, so an invalid form never reaches the transport's submit handler */
    form.addEventListener(
      'submit',
      (event) => {
        if (submitButton.getAttribute('aria-disabled') === 'true') {
          event.preventDefault();
          event.stopImmediatePropagation();

          return;
        }

        const firstInvalid = validate();

        if (!firstInvalid) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        firstInvalid.focus({ preventScroll: true });
        anchorOf(firstInvalid).scrollIntoView({ block: 'center', behavior: 'smooth' });
      },
      true
    );

    transport({ form, setBusy, showSuccess, showError, hideError });

    setBusy(false);
    syncConditionals();
    syncCounters();
  }

  document.querySelectorAll('[data-contact-root]').forEach(init);
})();
